import { Link, useParams } from "react-router-dom";
import { GAMES, PUBLIC_GAMES, bestText } from "../lib/room";
import { gameOrder, usePopular } from "../lib/popular";
import { PopularTag } from "../components/ui";
import { GUIDES, gameBySlug, gamePath, gameSeo, useSeo } from "../lib/seo";
import { useCreateRoom } from "../components/CreateRoom";
import AdSlot from "../components/AdSlot";
import { useI18n } from "../i18n";
import { withLang } from "../i18n/paths";
import GameIcon from "../components/GameIcon";
import Pic from "../components/pics";

// A page per game (/games/last-card, …): what it is and how to play, so people
// searching for a game like it can find it, then start a room in one tap.
export default function GamePage() {
  const { t, tx, lang } = useI18n();
  const { slug } = useParams();
  const id = gameBySlug(slug);
  const { create, busy, modal } = useCreateRoom();
  const top = usePopular();
  const page = id ? gameSeo(lang, id) : null;
  useSeo(
    page
      ? { title: page.title, description: page.description, path: gamePath(id), lang }
      : { title: `${t("Game not found")} - PartyCade`, noindex: true }
  );

  if (!page) {
    return (
      <div className="room-message panel">
        <div className="room-message-emoji">
        <Pic name="cards" />
      </div>
        <h2>{t("We don't have that game")}</h2>
        <Link to={withLang(lang, "/")} className="btn btn-primary">
          {t("See all games")}
        </Link>
      </div>
    );
  }

  const g = GAMES[id];
  const name = t(g.name);
  return (
    <article className="game-page" style={{ "--accent": g.color }}>
      <header className="game-page-hero">
        {top.includes(id) && <PopularTag />}
        <h1>
          <GameIcon id={id} /> {name}
        </h1>
        <p>{t(g.tagline)}</p>
        <button className="btn btn-lg" onClick={() => create(id)} disabled={busy === id}>
          {busy === id ? t("Shuffling…") : t("Play {game} free", { game: name })}
        </button>
      </header>

      <section className="panel">
        <p>{page.intro}</p>
        <p>
          <b>{t("Players:")}</b> {t("{min}+ players", { min: g.minPlayers })} ({bestText(g, t)}). <b>{t("Price:")}</b>{" "}
          {t("free, in your browser, no download.")}
        </p>
      </section>

      <section className="panel">
        <h2>{t("How to play {game}", { game: name })}</h2>
        <ol>
          {page.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>

      <section className="panel">
        <h2>{t("How to start a game")}</h2>
        <ol>
          <li>{tx("Tap <b>{button}</b> above to make a room.", { button: t("Play {game} free", { game: name }) })}</li>
          <li>{t("Send the room link to your friends in a group chat, a text or on Discord.")}</li>
          <li>{t("Everyone joins from their own phone or computer, as a guest or with an account. The host taps Start.")}</li>
        </ol>
      </section>

      {lang === "en" && page.rules && (
        <section className="panel">
          <h2>{name} rules in detail</h2>
          {page.rules.map((rule) => (
            <p key={rule}>{rule}</p>
          ))}
        </section>
      )}

      {lang === "en" && page.tips && (
        <section className="panel">
          <h2>Tips for {name}</h2>
          <ul>
            {page.tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </section>
      )}

      {lang === "en" && page.faq && (
        <section className="panel faq">
          <h2>{name} questions</h2>
          {page.faq.map(({ q, a }) => (
            <details key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </section>
      )}

      {lang === "en" && (
        <section className="panel">
          <h2>Guides</h2>
          <p>
            {GUIDES.filter((guide) => guide.games.includes(id)).map((guide, i) => (
              <span key={guide.slug}>
                {i > 0 && " · "}
                <Link to={`/guides/${guide.slug}`}>{guide.title}</Link>
              </span>
            ))}
            {!GUIDES.some((guide) => guide.games.includes(id)) && <Link to="/guides">All guides</Link>}
          </p>
        </section>
      )}

      <AdSlot kind="banner" />

      <nav className="panel" aria-label={t("More games")}>
        <h2>{t("More party games")}</h2>
        <div className="game-page-more">
          {gameOrder(Object.keys(PUBLIC_GAMES), top)
            .filter((other) => other !== id)
            .map((other) => (
              <Link key={other} to={gamePath(other, lang)}>
                <GameIcon id={other} /> {t(GAMES[other].name)}
              </Link>
            ))}
        </div>
      </nav>
      {modal}
    </article>
  );
}
